import { AxiosError } from 'axios'
import { create } from 'zustand'

import { deleteProduct, getEpisodeById, getProductById, getProducts, getTags } from '@/api/api'
import { Episode, EpisodeList, GetProductsQuery, GetProductsResponse, Product, Tag } from '@/api/type'

import { useAppStore } from './useAppStore'

type MediaState = {
  domain: string
  products?: GetProductsResponse
  episodes?: Product
  episode?: Episode
  tags: Tag[]
  /** tags がどのドメインのものかを保持する */
  tagsDomain: string
  getProducts: (option?: GetProductsQuery, callback?: (res: GetProductsResponse) => void, showLoading?: boolean) => void
  getProductById: (productId: number, callback?: (res: Product) => void, showLoading?: boolean) => void
  getEpisodeById: (episodeId: number, callback?: (res: Episode) => void, showLoading?: boolean) => void
  getTags: (domain: string, callback?: () => void, showLoading?: boolean) => void
  deleteProduct: (productId: number, callback?: () => void) => void
  /** productsストア内の特定エピソードの進捗をインプレース更新 */
  updateEpisodeProgress: (episodeId: number, progress: number, isCompleted?: boolean) => void
  /** productsストア内の特定エピソードのお気に入り状態をインプレース更新 */
  updateEpisodeFavorite: (episodeId: number, isFavorite: boolean) => void
  /** productsストア内の特定プロダクトのタイトルをインプレース更新 */
  updateProductTitle: (productId: number, title: string) => void
  errorMessages: string
  resetEpisode: () => void
  setDomain: (domain: string) => void
}
const { startLoading, stopLoading } = useAppStore.getState()
export const useMediaStore = create<MediaState>((set) => ({
  domain: '',
  tags: [],
  tagsDomain: '',
  errorMessages: '',

  setDomain: (domain: string) => {
    set(() => ({ domain }))
  },

  getProducts: async (
    option?: GetProductsQuery,
    callback?: (res: GetProductsResponse) => void,
    showLoading: boolean = true,
  ) => {
    if (showLoading) startLoading()
    try {
      const response = await getProducts(option)
      set(() => ({ products: response }))
      // オプションにdomainが指定されていれば設定（undefinedも含む）
      if (option !== undefined && 'domain' in option) {
        set(() => ({ domain: option.domain ?? '' }))
      }
      if (showLoading) stopLoading()
      callback?.(response)
    } catch (error) {
      if (error instanceof AxiosError) {
        set(() => ({ errorMessages: error.message }))
        if (showLoading) stopLoading()
      }
      console.error('Error fetching data:', error)
    }
  },

  getProductById: async (productId: number, callback?: (res: Product) => void, showLoading = false) => {
    if (showLoading) startLoading()
    try {
      const response = await getProductById(productId)
      set(() => ({ episodes: response }))
      if (showLoading) stopLoading()
      callback?.(response)
    } catch (error) {
      if (error instanceof AxiosError) {
        set(() => ({ errorMessages: error.message }))
        if (showLoading) stopLoading()
      }
      console.error('Error fetching data:', error)
    }
  },

  getEpisodeById: async (episodeId: number, callback?: (res: Episode) => void, showLoading = false) => {
    if (showLoading) startLoading()
    try {
      const response = await getEpisodeById(episodeId)
      set(() => ({ episode: response }))
      if (showLoading) stopLoading()
      callback?.(response)
    } catch (error) {
      if (error instanceof AxiosError) {
        set(() => ({ errorMessages: error.message }))
        if (showLoading) stopLoading()
      }
      console.error('Error fetching data:', error)
    }
  },

  getTags: async (domain: string, callback?: () => void, showLoading: boolean = true) => {
    if (showLoading) startLoading()
    try {
      const response = await getTags(domain)
      set(() => ({ tags: response.tags, tagsDomain: domain }))
      if (showLoading) stopLoading()
      callback?.()
    } catch (error) {
      if (error instanceof AxiosError) {
        set(() => ({ errorMessages: error.message }))
        if (showLoading) stopLoading()
      }
      console.error('Error fetching data:', error)
    }
  },

  deleteProduct: async (productId: number, callback?: () => void) => {
    startLoading()
    try {
      await deleteProduct(productId)
      stopLoading()
      callback?.()
    } catch (error) {
      if (error instanceof AxiosError) {
        set(() => ({ errorMessages: error.message }))
        stopLoading()
      }
      console.error('Error fetching data:', error)
    }
  },

  updateEpisodeProgress: (episodeId: number, progress: number, isCompleted = false) => {
    const patchEpisodes = (episodes: EpisodeList[]) =>
      episodes.map((ep) => (ep.episode_id === episodeId ? { ...ep, progress, is_completed: isCompleted } : ep))

    set((state) => {
      const updates: Partial<MediaState> = {}

      if (state.products) {
        let found = false
        const updatedProducts = state.products.products.map((product) => {
          if (!product.episodes.some((ep) => ep.episode_id === episodeId)) return product
          found = true
          return { ...product, episodes: patchEpisodes(product.episodes) }
        })
        if (found) updates.products = { ...state.products, products: updatedProducts }
      }

      if (state.episodes?.episodes.some((ep) => ep.episode_id === episodeId)) {
        updates.episodes = { ...state.episodes, episodes: patchEpisodes(state.episodes.episodes) }
      }

      return updates
    })
  },

  updateEpisodeFavorite: (episodeId: number, isFavorite: boolean) => {
    set((state) => {
      if (!state.products) return state
      let found = false
      const updatedProducts = state.products.products.map((product) => {
        const target = product.episodes.find((ep) => ep.episode_id === episodeId)
        if (!target || target.is_favorite === isFavorite) return product
        found = true
        return {
          ...product,
          favorite_episode_count: Math.max(0, product.favorite_episode_count + (isFavorite ? 1 : -1)),
          episodes: product.episodes.map((ep) =>
            ep.episode_id === episodeId ? { ...ep, is_favorite: isFavorite } : ep,
          ),
        }
      })
      if (!found) return state
      return { products: { ...state.products, products: updatedProducts } }
    })
  },

  updateProductTitle: (productId: number, title: string) => {
    set((state) => {
      const updates: Partial<MediaState> = {}
      if (state.products?.products.some((p) => p.product_id === productId)) {
        updates.products = {
          ...state.products,
          products: state.products.products.map((p) => (p.product_id === productId ? { ...p, title } : p)),
        }
      }
      if (state.episodes?.product_id === productId) {
        updates.episodes = { ...state.episodes, title }
      }
      return updates
    })
  },

  resetEpisode: () => {
    set(() => ({ episode: undefined }))
  },
}))
